import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const DiscoverServices = () => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollContainerRef = useRef(null);

  const handleCTAClick = () => {
    navigate('/services');
  };

  const services = [
    {
      title: 'Websites & Apps',
      items: [
        'Custom Website Creation (fast, secure, SEO-optimized)',
        'Mobile Apps (iOS, Android, Web Apps)',
        'E-commerce & Platforms'
      ]
    },
    {
      title: 'Brand & Content',
      items: [
        'Branding & full visual identity',
        'Content Creation (copy, blogs, SEO content, social)',
        'Graphic Design (Stationary, posters, flyers, print)',
        'Print & packaging (labels, personalized prints)'
      ]
    },
    {
      title: 'Visual Media',
      items: [
        'Photography (product, team, event)',
        'Videography (explainer videos, ads, reels)',
        'Motion Graphics (animated logos, promos)'
      ]
    }
  ];

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const cardWidth = scrollContainerRef.current.offsetWidth * 0.8; // 80% of container width
      const index = Math.round(scrollLeft / cardWidth);
      setActiveIndex(index);
    }
  };

  const scrollToCard = (index) => {
    if (scrollContainerRef.current) {
      const cardWidth = scrollContainerRef.current.offsetWidth * 0.8;
      scrollContainerRef.current.scrollTo({
        left: cardWidth * index,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <section className="py-12 md:py-10 px-4 md:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 md:mb-16">
          <div className="inline-flex items-center justify-center px-3 py-1 md:px-4 md:py-2 rounded-full bg-soft-gray gap-2 mb-3 md:mb-4 mx-auto">
            <div className="w-2 h-2 md:w-3 md:h-3 bg-primary-blue rounded-full"></div>
            <h2 className="text-lg md:text-2xl lg:text-xl font-normal text-gray-800">
              Discover our services
            </h2>
          </div>
          <p className="text-sm md:text-lg text-gray-500 max-w-xl mx-auto mb-3 md:mb-4 px-4">
            All under one roof. One team. One creative ecosystem.
          </p>
          <button shadow
              className="inline-flex max-md:hidden items-center gap:1 md:gap-1 bg-primary-blue text-white border-0 pl-3 md:pl-4 pr-2 rounded-full text-lg font-semibold cursor-pointer transition-all duration-300 -[0_10px_20px_rgba(29,68,233,0.3)] hover:bg-blue-700 hover:-translate-y-1 hover:shadow-[0_15px_30px_rgba(29,68,233,0.4)] active:translate-y-0 group"
              onClick={handleCTAClick}
            >
              <span className="py-1 md:py-2 text-xs md:text-base">See all</span>
              <div className="flex items-center justify-center h-6 w-6 md:w-8 md:h-8 bg-white rounded-full transition-transform duration-300 group-hover:translate-x-1 ml-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path 
                    d="M5 12h14M12 5l7 7-7 7" 
                    stroke="#000000ff" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </button>
        </div>

        {/* Mobile Carousel - visible only on mobile */}
        <div className="lg:hidden relative flex-col flex justify-center">
          <div 
            ref={scrollContainerRef}
            className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-3 px-2 py-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {services.map((service, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-[80%] snap-center"
              >
                <div 
                  className="bg-white rounded-[30px] py-6 px-4 transition-shadow duration-300 h-full"
                  style={{ boxShadow: '0 0 10px rgba(0, 0, 0, 0.06)' }}
                >
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <h3 className="text-lg font-bold text-gray-800">{service.title}</h3>
                  </div>
                  <ul className="space-y-0">
                    {service.items.map((item, itemIndex) => (
                      <li 
                        key={itemIndex}
                        className={`flex items-start gap-2 py-3 ${
                          itemIndex < service.items.length - 1 ? 'border-b border-gray-200' : ''
                        }`}
                      >
                        <div className="w-2 h-2 bg-primary-blue rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center gap-1.5 mt-4">
            {services.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToCard(index)}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  activeIndex === index 
                    ? 'bg-primary-blue' 
                    : 'bg-gray-300'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
          <div className="flex justify-center mt-6">
            <button shadow
                className="inline-flex items-center gap:1 md:gap-1 bg-primary-blue text-white border-0 pl-3 md:pl-4 pr-2 rounded-full text-lg font-semibold cursor-pointer transition-all duration-300 -[0_10px_20px_rgba(29,68,233,0.3)] hover:bg-blue-700 hover:-translate-y-1 hover:shadow-[0_15px_30px_rgba(29,68,233,0.4)] active:translate-y-0 group"
                onClick={handleCTAClick}
              >
                <span className="py-2 text-xs md:text-base">See all</span>
                <div className="flex items-center justify-center h-6 w-6 md:w-8 md:h-8 bg-white rounded-full transition-transform duration-300 group-hover:translate-x-1 ml-2">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path 
                      d="M5 12h14M12 5l7 7-7 7" 
                      stroke="#000000ff" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </button>
          </div>
        </div>

        {/* Desktop Grid - visible only on desktop */}
        <div className="hidden lg:grid px-14 grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-center">
          
          {/* Websites & Apps */}
          <div 
            className="bg-white rounded-[50px] py-6 px-6 transition-shadow duration-300"
            style={{ boxShadow: '0 0 20px rgba(0, 0, 0, 0.1)' }}
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <h3 className="text-2xl font-bold text-gray-800">Websites & Apps</h3>
             
            </div>
            <ul className="space-y-0">
              <li className="flex items-start gap-3 py-4 border-b border-gray-200">
                <div className="w-3 h-3 bg-primary-blue rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-600">Custom Website Creation (fast, secure, SEO-optimized)</span>
              </li>
              <li className="flex items-start gap-3 py-4 border-b border-gray-200">
                <div className="w-3 h-3 bg-primary-blue rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-600">Mobile Apps (iOS, Android, Web Apps)</span>
              </li>
              <li className="flex items-start gap-3 py-4">
                <div className="w-3 h-3 bg-primary-blue rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-600">E-commerce & Platforms</span>
              </li>
            </ul>
          </div>

          {/* Brand & Content - Taller center card */}
          <div 
            className="bg-white rounded-[50px] py-6 px-6 transition-shadow duration-300"
            style={{ boxShadow: '0 0 20px rgba(0, 0, 0, 0.1)' }}
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <h3 className="text-2xl font-bold justify-center text-gray-800">Brand & Content</h3>
              
            </div>
            <ul className="space-y-0">
              <li className="flex items-start gap-3 py-4 border-b border-gray-200">
                <div className="w-3 h-3 bg-primary-blue rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-600">Branding & full visual identity</span>
              </li>
              <li className="flex items-start gap-3 py-4 border-b border-gray-200">
                <div className="w-3 h-3 bg-primary-blue rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-600">Content Creation (copy, blogs, SEO content, social)</span>
              </li>
              <li className="flex items-start gap-3 py-4 border-b border-gray-200">
                <div className="w-3 h-3 bg-primary-blue rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-600">Graphic Design (Stationary, posters, flyers, print)</span>
              </li>
              <li className="flex items-start gap-3 py-4">
                <div className="w-3 h-3 bg-primary-blue rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-600">Print & packaging (labels, personalized prints)</span>
              </li>
            </ul>
          </div>

          {/* Visual Media */}
          <div 
            className="bg-white rounded-[50px] py-6 px-6 transition-shadow duration-300"
            style={{ boxShadow: '0 0 20px rgba(0, 0, 0, 0.1)' }}
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <h3 className="text-2xl font-bold text-gray-800">Visual Media</h3>
            </div>
            <ul className="space-y-0">
              <li className="flex items-start gap-3 py-4 border-b border-gray-200">
                <div className="w-3 h-3 bg-primary-blue rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-600">Photography (product, team, event)</span>
              </li>
              <li className="flex items-start gap-3 py-4 border-b border-gray-200">
                <div className="w-3 h-3 bg-primary-blue rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-600">Videography (explainer videos, ads, reels)</span>
              </li>
              <li className="flex items-start gap-3 py-4">
                <div className="w-3 h-3 bg-primary-blue rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-600">Motion Graphics (animated logos, promos)</span>
              </li>
            </ul>
          </div>

        </div>
      </div>
    </section>
  );
};

export default DiscoverServices;